import { ChangeEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { FormGroup, Input } from 'src/components/ui';
import { JnxExpr, useJnx } from '../../util/jnx';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import getPathFromId from '../../util/getPathFromId';
import { FormFieldProps } from '../FormComponentTypes';
import { JSONObject } from '../../api/types';
import useTestId from '../../hooks/useTestId';
import useWrapInput from './utils/useWrapInput';

type CheckboxFieldProps = FormFieldProps<
  boolean,
  {},
  {
    'ui:checkboxHidden'?: boolean;
    'ui:onCheck'?: JnxExpr<void | Promise<void>>;
    'ui:onUncheck'?: JnxExpr<void | Promise<void>>;
    'ui:defaultValueExpr'?: JnxExpr<void | Promise<void>>;
    'ui:prefix'?: string;
    'ui:sufix'?: string;
  }
>;

function CheckboxField(props: CheckboxFieldProps) {
  const {
    formData,
    registry,
    disabled,
    fieldPathId: { $id, path },
    schema: { title },
    uiSchema: {
      'ui:readonly': uiReadonly,
      'ui:checkboxHidden': checkboxHidden,
      'ui:onCheck': uiOnCheck,
      'ui:onUncheck': uiOnUncheck,
      'ui:defaultValueExpr': uiDefaultValueExpr,
      'ui:prefix': prefix,
      'ui:sufix': sufix,
    },
    required,
    readonly: propReadOnly,
    onChange: propOnChange,
  } = props;
  const formContext = registry?.formContext;
  const { setFormDataValues, sideChannel } = formContext;

  const dataPath = useMemo(() => getPathFromId($id), [$id]);
  const readonly = uiReadonly || propReadOnly;
  const testIdProps = useTestId($id);

  const rootFormData = useSideChannelSubscription(sideChannel, 0);
  const rootFormDataRef = useRef<JSONObject>(rootFormData);
  rootFormDataRef.current = rootFormData;

  const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
  const onCheckJnx = useJnx(uiOnCheck, { functionBinds });
  const onUncheckJnx = useJnx(uiOnUncheck, { functionBinds });

  const defaultValueJnx = useJnx(uiDefaultValueExpr);
  const defaultValue = useMemo(
    () =>
      defaultValueJnx &&
      defaultValueJnx.eval(rootFormData || {}, '', {
        root: rootFormData,
        formContext: props.formContext,
      }),
    [rootFormData, props.formContext, defaultValueJnx]
  );

  const currentValue = useMemo(() => {
    if (formData == null) return defaultValue;
    return formData;
  }, [formData, defaultValue]);

  const [defaultValueSet, setDefaultValueSet] = useState<boolean>(false);
  useEffect(() => {
    if (!path) return;
    if (defaultValue == null) return;
    if (formData != null) {
      setDefaultValueSet(true);
      return;
    }

    setTimeout(() => {
      propOnChange(!!defaultValue, path);
      setDefaultValueSet(true);
    }, 500);
  }, [defaultValue, formData, path, defaultValueSet]);

  const onChange: ChangeEventHandler<HTMLInputElement> = ({ target: { checked } }) => {
    propOnChange(checked, path);

    if (checked && onCheckJnx) {
      setTimeout(() => {
        onCheckJnx.eval(rootFormDataRef.current, dataPath, {
          rootData: rootFormDataRef.current,
          fieldPath: dataPath,
        });
      }, 500);
    }

    if (!checked && onUncheckJnx) {
      setTimeout(() => {
        onUncheckJnx.eval(rootFormDataRef.current, dataPath, {
          rootData: rootFormDataRef.current,
          fieldPath: dataPath,
        });
      }, 500);
    }
  };

  const checkbox = (
    <FormGroup disabled={readonly || disabled} check className="h-[38px]">
      <label className={' checkbox '} htmlFor={$id}>
        <Input
          id={$id}
          {...testIdProps}
          type="checkbox"
          disabled={readonly || disabled}
          required={required}
          checked={!!currentValue}
          onChange={onChange}
        />
        {!prefix && !sufix ? title : <Trans>Yes</Trans>}
      </label>
    </FormGroup>
  );

  return useWrapInput({
    input: checkbox,
    fieldId: $id,
    formData,
    fieldSchema: props.schema,
    formContext,
    prefix,
    sufix,
  });
}

export default CheckboxField;
